import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../App';
import { api } from '../api';
import { toast } from 'sonner';
import {
  Search, ChevronUp, ChevronDown, Check, History, RotateCcw, Eye,
  EyeOff, Printer, Filter
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import PageLayout from '../components/PageLayout';

// ── Constants ─────────────────────────────────────────────
const OPERATIONS = {
  count_update:   { label: 'Count Update',    desc: 'Set exact quantities found during a physical count' },
  receive:        { label: 'Receive Inventory', desc: 'Add quantities received from suppliers or transfers' },
  donate_dispose: { label: 'Donate / Dispose',  desc: 'Remove quantities donated, expired, or damaged' },
};

const LOCATIONS = {
  us_warehouse: { label: 'US Warehouse', field: 'qty_us_warehouse' },
  guatemala:    { label: 'Guatemala',    field: 'qty_guatemala' },
  in_bags:      { label: 'In Bags',      field: 'qty_in_bags' },
};

const DISPOSE_REASONS = ['donated', 'expired', 'damaged', 'lost', 'other'];

// Generate bag number suggestions 1-50 + A1-A30
const BAG_SUGGESTIONS = [
  ...Array.from({ length: 50 }, (_, i) => String(i + 1)),
  ...Array.from({ length: 30 }, (_, i) => `A${i + 1}`)
];

const CATEGORIES = ['supplies', 'medications', 'equipment'];
const CAT_LABELS = { supplies: 'Supplies', medications: 'Medications', equipment: 'Equipment' };

// ── Diff badge ─────────────────────────────────────────────
const DiffBadge = ({ diff }) => {
  if (!diff && diff !== 0) return null;
  if (diff === 0) return <span className="text-xs text-slate-400">—</span>;
  const pos = diff > 0;
  return (
    <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${pos ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
      {pos ? '+' : ''}{diff}
    </span>
  );
};

// ── BagInput: autocomplete for bag numbers ─────────────────
const BagInput = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 0 });
  const inputRef = React.useRef(null);

  const filtered = value
    ? BAG_SUGGESTIONS.filter(b => b.toLowerCase().startsWith(value.toLowerCase())).slice(0, 8)
    : BAG_SUGGESTIONS.slice(0, 8);

  const handleFocus = () => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setCoords({ top: rect.bottom + window.scrollY + 2, left: rect.left + window.scrollX, width: rect.width });
    }
    setOpen(true);
  };

  return (
    <div className="relative inline-block">
      <Input
        ref={inputRef}
        value={value}
        onChange={e => { onChange(e.target.value); setOpen(true); }}
        onFocus={handleFocus}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder="Bag/Box #"
        className="h-7 text-xs w-24 text-center"
      />
      {open && filtered.length > 0 && (
        <div
          className="fixed z-[9999] bg-white border border-slate-200 rounded-lg shadow-xl py-1 min-w-[80px]"
          style={{ top: coords.top, left: coords.left, width: Math.max(coords.width, 80) }}
        >
          {filtered.map(b => (
            <button
              key={b}
              className="w-full text-left px-3 py-1 text-xs hover:bg-slate-50"
              onMouseDown={() => { onChange(b); setOpen(false); }}
            >
              {b}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ────────────────────────────────────────────────────────────
// Main Component
// ────────────────────────────────────────────────────────────
const InventoryOperations = () => {
  const [inventoryItems, setInventoryItems] = useState([]);
  const [types, setTypes] = useState([]);
  const [operationType, setOperationType] = useState('count_update');
  const [location, setLocation] = useState('guatemala');
  const [notes, setNotes] = useState('');
  const [activeCategory, setActiveCategory] = useState('supplies');
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [showChangedOnly, setShowChangedOnly] = useState(false);
  const [sortBy, setSortBy] = useState('name');
  const [sortAsc, setSortAsc] = useState(true);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);

  // Per-item state: { [item_id]: { newQty, qtyChange, bagNumber, reason } }
  const [edits, setEdits] = useState({});

  useEffect(() => {
    Promise.all([api.get('/inventory/items'), api.get('/inventory/types')]).then(([itemsRes, typesRes]) => {
      setInventoryItems(itemsRes.data);
      setTypes(typesRes.data);
      setLoading(false);
    }).catch(() => { toast.error('Failed to load inventory'); setLoading(false); });
  }, []);

  const fetchHistory = async () => {
    try {
      const res = await api.get('/inventory/operations?limit=20');
      setHistory(res.data);
      setShowHistory(true);
    } catch { toast.error('Failed to load history'); }
  };

  // ── Edit helpers ────────────────────────────────────────
  const setEdit = (itemId, key, value) =>
    setEdits(prev => ({ ...prev, [itemId]: { ...prev[itemId], [key]: value } }));

  const clearEdits = () => setEdits({});

  const getNewQty = (item) => edits[item.item_id]?.newQty ?? '';
  const getQtyChange = (item) => edits[item.item_id]?.qtyChange ?? '';
  const getBagNumber = (item) => edits[item.item_id]?.bagNumber ?? '';
  const getReason = (item) => edits[item.item_id]?.reason ?? 'donated';

  const currentQty = (item) => item[LOCATIONS[location]?.field] ?? 0;

  const getDiff = useCallback((item) => {
    const cur = item[LOCATIONS[location]?.field] ?? 0;
    if (operationType === 'count_update') {
      const nq = edits[item.item_id]?.newQty;
      return nq !== '' && nq !== undefined ? parseInt(nq) - cur : null;
    }
    if (operationType === 'receive') {
      const qc = parseInt(edits[item.item_id]?.qtyChange || 0);
      return qc > 0 ? qc : null;
    }
    if (operationType === 'donate_dispose') {
      const qc = parseInt(edits[item.item_id]?.qtyChange || 0);
      return qc > 0 ? -qc : null;
    }
    return null;
  }, [edits, operationType, location]);

  const hasChange = useCallback((item) => {
    const diff = getDiff(item);
    return diff !== null && diff !== 0;
  }, [getDiff]);

  // ── Filtered + sorted items ─────────────────────────────
  const categoryItems = useMemo(() => inventoryItems.filter(i => {
    const type = types.find(t => t.type_id === i.type_id);
    return type?.category === activeCategory;
  }), [inventoryItems, types, activeCategory]);

  const visibleItems = useMemo(() => {
    let items = categoryItems;
    if (search) items = items.filter(i =>
      i.name.toLowerCase().includes(search.toLowerCase()) ||
      i.type_name?.toLowerCase().includes(search.toLowerCase())
    );
    if (filterType !== 'all') items = items.filter(i => i.type_id === filterType);
    if (showChangedOnly) items = items.filter(i => hasChange(i));
    items = [...items].sort((a, b) => {
      let va = a[sortBy] ?? ''; let vb = b[sortBy] ?? '';
      if (typeof va === 'string') va = va.toLowerCase();
      if (typeof vb === 'string') vb = vb.toLowerCase();
      return sortAsc ? (va < vb ? -1 : va > vb ? 1 : 0) : (va > vb ? -1 : va < vb ? 1 : 0);
    });
    return items;
  }, [categoryItems, search, filterType, showChangedOnly, sortBy, sortAsc, hasChange]);

  // ── Category types for filter ───────────────────────────
  const categoryTypes = useMemo(() =>
    types.filter(t => t.category === activeCategory),
    [types, activeCategory]
  );

  // ── Count changed items ─────────────────────────────────
  const changedItems = inventoryItems.filter(hasChange);

  // ── Sort header ─────────────────────────────────────────
  const SortHeader = ({ field, label, className = '' }) => (
    <th
      className={`px-3 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide cursor-pointer hover:text-primary select-none ${className}`}
      onClick={() => { if (sortBy === field) setSortAsc(!sortAsc); else { setSortBy(field); setSortAsc(true); } }}
    >
      <span className="flex items-center gap-1">
        {label}
        {sortBy === field ? (sortAsc ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />) : null}
      </span>
    </th>
  );

  // ── Apply ───────────────────────────────────────────────
  const handleApply = async () => {
    setApplying(true);
    try {
      const items = changedItems.map(item => {
        const base = { item_id: item.item_id, bag_number: getBagNumber(item) };
        if (operationType === 'count_update') return { ...base, new_qty: parseInt(edits[item.item_id]?.newQty) };
        if (operationType === 'receive') return { ...base, qty_change: parseInt(edits[item.item_id]?.qtyChange || 0) };
        if (operationType === 'donate_dispose') return { ...base, qty_change: parseInt(edits[item.item_id]?.qtyChange || 0), reason: getReason(item) };
        return base;
      });

      const res = await api.post('/inventory/operations', { operation_type: operationType, location, notes, items });
      toast.success(res.data.message);
      setShowConfirm(false);
      clearEdits();
      const itemsRes = await api.get('/inventory/items');
      setInventoryItems(itemsRes.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Operation failed');
    } finally {
      setApplying(false);
    }
  };

  const countActions = (
    <>
      <Button variant="ghost" size="sm" onClick={fetchHistory} className="text-slate-500 h-8 text-xs">
        <History className="w-3.5 h-3.5 mr-1" />History
      </Button>
      <a href={`${process.env.REACT_APP_BACKEND_URL}/api/inventory/count-sheet?location=${location}&category=${activeCategory}`}
        target="_blank" rel="noopener noreferrer">
        <Button variant="outline" size="sm" className="h-8 text-xs rounded-full" data-testid="print-count-sheet-btn">
          <Printer className="w-3.5 h-3.5 mr-1" />Print Sheet
        </Button>
      </a>
    </>
  );

  return (
    <PageLayout title="Inventory Operations" headerActions={countActions} padded={false}>
      <div className="px-4 sm:px-6 py-3 space-y-3 border-b border-slate-200 bg-white">
            {/* ── Operation config bar ── */}
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label className="text-xs text-slate-500 block mb-0.5">Operation</label>
                <Select value={operationType} onValueChange={v => { setOperationType(v); clearEdits(); }}>
                  <SelectTrigger className="h-8 text-sm w-44 sm:w-52" data-testid="operation-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(OPERATIONS).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-slate-500 block mb-0.5">Location</label>
                <Select value={location} onValueChange={v => { setLocation(v); clearEdits(); }}>
                  <SelectTrigger className="h-8 text-sm w-36 sm:w-44" data-testid="location-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(LOCATIONS).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1 min-w-36">
                <label className="text-xs text-slate-500 block mb-0.5">Notes</label>
                <Input
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  placeholder="Optional notes..."
                  className="h-8 text-sm"
                  data-testid="notes-input"
                />
              </div>
            </div>

            {/* ── Apply Changes — full-width row so always reachable on mobile ── */}
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                {changedItems.length > 0 && (
                  <Badge className="bg-primary/10 text-primary border-0">
                    {changedItems.length} item{changedItems.length !== 1 ? 's' : ''} changed
                  </Badge>
                )}
                {changedItems.length > 0 && (
                  <Button variant="ghost" size="sm" onClick={clearEdits} className="text-slate-400 h-8 text-xs">
                    <RotateCcw className="w-3.5 h-3.5 mr-1" />Clear
                  </Button>
                )}
              </div>
              <Button
                size="sm"
                className={`rounded-full h-10 min-w-[130px] transition-opacity ${changedItems.length === 0 ? 'opacity-40 cursor-not-allowed bg-primary' : 'bg-primary'}`}
                disabled={changedItems.length === 0 || applying}
                onClick={() => setShowConfirm(true)}
                data-testid="apply-changes-btn"
              >
                <Check className="w-3.5 h-3.5 mr-1.5" />
                Apply {changedItems.length > 0 ? `(${changedItems.length})` : 'Changes'}
              </Button>
            </div>
          </div>

        <div className="px-4 sm:px-6 py-4">
          {/* ── Operation hint ── */}
          <p className="text-xs text-slate-500 mb-4 bg-slate-100 px-3 py-2 rounded-lg">
            <strong>{OPERATIONS[operationType]?.label}</strong> — {OPERATIONS[operationType]?.desc}.{' '}
            Location: <strong>{LOCATIONS[location]?.label}</strong>
            {location === 'guatemala' && ' · Enter bag/container numbers to track where items are stored.'}
          </p>

          {/* ── Category tabs ── */}
          <Tabs value={activeCategory} onValueChange={v => { setActiveCategory(v); setFilterType('all'); }}>
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <TabsList>
                {CATEGORIES.map(cat => {
                  const catItems = inventoryItems.filter(i => types.find(t => t.type_id === i.type_id)?.category === cat);
                  const catChanged = catItems.filter(hasChange).length;
                  return (
                    <TabsTrigger key={cat} value={cat} className="text-sm" data-testid={`tab-${cat}`}>
                      {CAT_LABELS[cat]}
                      {catChanged > 0 && (
                        <span className="ml-1.5 text-xs bg-primary text-white rounded-full px-1.5 py-0.5 leading-none">{catChanged}</span>
                      )}
                    </TabsTrigger>
                  );
                })}
              </TabsList>

              {/* Filter row */}
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                  <Input placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)}
                    className="pl-8 h-8 text-sm w-44" data-testid="search-input" />
                </div>
                <Select value={filterType} onValueChange={setFilterType}>
                  <SelectTrigger className="h-8 text-sm w-36" data-testid="type-filter">
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    {categoryTypes.map(t => (
                      <SelectItem key={t.type_id} value={t.type_id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  variant={showChangedOnly ? 'default' : 'outline'}
                  size="sm"
                  className={`h-8 text-xs rounded-full ${showChangedOnly ? 'bg-primary' : ''}`}
                  onClick={() => setShowChangedOnly(!showChangedOnly)}
                  data-testid="changed-only-btn"
                >
                  {showChangedOnly ? <Eye className="w-3.5 h-3.5 mr-1" /> : <EyeOff className="w-3.5 h-3.5 mr-1" />}
                  Changed only
                </Button>
              </div>
            </div>

            {CATEGORIES.map(cat => (
              <TabsContent key={cat} value={cat} className="mt-0">
                {loading ? (
                  <div className="py-12 text-center text-slate-400">Loading inventory...</div>
                ) : (
                  <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                   <div className="overflow-x-auto">
                    <table className="w-full text-sm min-w-[700px]" style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
                      <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                          <SortHeader field="name" label="Item" className="w-56" />
                          <SortHeader field="type_name" label="Type" className="w-28" />
                          <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide w-16">Unit</th>
                          <th className="px-3 py-2.5 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide w-24">
                            Current <span className="text-primary font-normal normal-case">({LOCATIONS[location]?.label})</span>
                          </th>
                          {location === 'guatemala' && (
                            <th className="px-3 py-2.5 text-center text-xs font-semibold text-slate-500 uppercase tracking-wide w-28">Bag / Box #</th>
                          )}
                          <th className="px-3 py-2.5 text-center text-xs font-semibold text-slate-500 uppercase tracking-wide w-28">
                            {operationType === 'count_update' ? 'New Count' :
                             operationType === 'receive' ? 'Qty Received' : 'Qty to Remove'}
                          </th>
                          {operationType === 'donate_dispose' && (
                            <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide w-28">Reason</th>
                          )}
                          <th className="px-3 py-2.5 text-center text-xs font-semibold text-slate-500 uppercase tracking-wide w-20">Change</th>
                          <th className="px-3 py-2.5 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide w-20">New Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibleItems.length === 0 ? (
                          <tr>
                            <td colSpan={9} className="py-10 text-center text-slate-400 text-sm">
                              {showChangedOnly ? 'No changed items in this category' : 'No items found'}
                            </td>
                          </tr>
                        ) : (
                          visibleItems.map((item, idx) => {
                            const cur = currentQty(item);
                            const diff = getDiff(item);
                            const changed = diff !== null && diff !== 0;
                            const newTotal = diff !== null ? cur + diff : cur;

                            return (
                              <tr
                                key={item.item_id}
                                className={`border-b border-slate-100 last:border-0 transition-colors
                                  ${changed ? 'bg-primary/3 hover:bg-primary/5' : idx % 2 === 0 ? 'bg-white hover:bg-slate-50' : 'bg-slate-50/50 hover:bg-slate-100/50'}`}
                                data-testid={`item-row-${item.item_id}`}
                              >
                                <td className="px-3 py-2 font-medium text-slate-800">{item.name}</td>
                                <td className="px-3 py-2 text-slate-500 text-xs">{item.type_name}</td>
                                <td className="px-3 py-2 text-slate-400 text-xs">{item.unit_of_measurement || '—'}</td>
                                <td className="px-3 py-2 text-right font-semibold text-slate-700">{cur}</td>

                                {/* Bag/Box # (Guatemala only) */}
                                {location === 'guatemala' && (
                                  <td className="px-3 py-2 text-center">
                                    <BagInput
                                      value={getBagNumber(item)}
                                      onChange={v => setEdit(item.item_id, 'bagNumber', v)}
                                    />
                                  </td>
                                )}

                                {/* Count/Qty field */}
                                <td className="px-3 py-2 text-center">
                                  {operationType === 'count_update' ? (
                                    <Input
                                      type="number"
                                      min="0"
                                      placeholder={String(cur)}
                                      value={getNewQty(item)}
                                      onChange={e => setEdit(item.item_id, 'newQty', e.target.value)}
                                      className={`h-7 w-20 text-center mx-auto text-sm ${changed ? 'border-primary ring-1 ring-primary/30' : ''}`}
                                      data-testid={`qty-input-${item.item_id}`}
                                    />
                                  ) : (
                                    <Input
                                      type="number"
                                      min="0"
                                      placeholder="0"
                                      value={getQtyChange(item)}
                                      onChange={e => setEdit(item.item_id, 'qtyChange', e.target.value)}
                                      className={`h-7 w-20 text-center mx-auto text-sm ${changed ? 'border-primary ring-1 ring-primary/30' : ''}`}
                                      data-testid={`qty-input-${item.item_id}`}
                                    />
                                  )}
                                </td>

                                {/* Reason (donate/dispose only) */}
                                {operationType === 'donate_dispose' && (
                                  <td className="px-3 py-2">
                                    <Select value={getReason(item)} onValueChange={v => setEdit(item.item_id, 'reason', v)}>
                                      <SelectTrigger className="h-7 text-xs w-28">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {DISPOSE_REASONS.map(r => (
                                          <SelectItem key={r} value={r} className="text-xs capitalize">{r}</SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </td>
                                )}

                                {/* Diff */}
                                <td className="px-3 py-2 text-center">
                                  <DiffBadge diff={diff} />
                                </td>

                                {/* New total */}
                                <td className={`px-3 py-2 text-right font-semibold ${changed ? (diff > 0 ? 'text-green-600' : 'text-red-600') : 'text-slate-400'}`}>
                                  {changed ? newTotal : '—'}
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                   </div>{/* close overflow-x-auto */}
                  </div>
                )}
              </TabsContent>
            ))}
          </Tabs>
        </div>

      {/* ── Confirm Apply Dialog ── */}
      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Confirm {OPERATIONS[operationType]?.label}</DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-3">
            <div className="bg-slate-50 rounded-xl p-4 text-sm space-y-2">
              <div className="flex justify-between">
                <span className="text-slate-500">Operation</span>
                <span className="font-medium">{OPERATIONS[operationType]?.label}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Location</span>
                <span className="font-medium">{LOCATIONS[location]?.label}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Items to update</span>
                <span className="font-semibold text-primary">{changedItems.length}</span>
              </div>
              {notes && <div className="flex justify-between">
                <span className="text-slate-500">Notes</span>
                <span className="font-medium text-right max-w-[200px] truncate">{notes}</span>
              </div>}
            </div>

            {/* Preview list */}
            <div className="max-h-48 overflow-y-auto border border-slate-200 rounded-lg text-sm">
              {changedItems.map(item => {
                const diff = getDiff(item);
                return (
                  <div key={item.item_id} className="flex items-center justify-between px-3 py-2 border-b border-slate-100 last:border-0">
                    <span className="text-slate-700 truncate mr-3">{item.name}</span>
                    <DiffBadge diff={diff} />
                  </div>
                );
              })}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirm(false)}>Cancel</Button>
            <Button
              className="bg-primary"
              disabled={applying}
              onClick={handleApply}
              data-testid="confirm-apply-btn"
            >
              {applying ? 'Applying...' : 'Confirm & Apply'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── History Dialog ── */}
      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Operation History</DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto space-y-2 py-2">
            {history.length === 0 ? (
              <p className="text-center text-slate-400 py-8">No operations yet</p>
            ) : (
              history.map(op => (
                <div key={op.operation_id} className="border border-slate-100 rounded-xl p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <span className="font-medium text-slate-800 text-sm">{OPERATIONS[op.operation_type]?.label || op.operation_type}</span>
                      <span className="mx-2 text-slate-300">·</span>
                      <span className="text-sm text-slate-500">{LOCATIONS[op.location]?.label || op.location}</span>
                    </div>
                    <span className="text-xs text-slate-400">{new Date(op.performed_at).toLocaleString()}</span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-slate-500">
                    <span>By: {op.performed_by_name}</span>
                    <span>·</span>
                    <span>{op.items_changed} item{op.items_changed !== 1 ? 's' : ''} changed</span>
                    {op.notes && <><span>·</span><span className="italic">{op.notes}</span></>}
                  </div>
                  {op.change_log?.length > 0 && (
                    <div className="mt-2 grid grid-cols-2 gap-1">
                      {op.change_log.map((c, i) => (
                        <div key={i} className="text-xs text-slate-600 flex justify-between bg-slate-50 px-2 py-1 rounded">
                          <span className="truncate mr-2">{c.name}</span>
                          <DiffBadge diff={c.diff} />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
};

export default InventoryOperations;
